import {PhaOptionHoldings, PhaPremiumRow, PhaProtectionRow, PhaSheet, PhaUnderlyingRow} from '../pha-sheet.model';
import {
    AnalysisBlockDto, AnalysisBlockInput, AnalysisBlockStatus, AnalysisChangeInput, AnalysisSection
} from '../../shell-communication/analysis-session-operations';
import {DateTime} from 'luxon';
import {duplicateContracts} from '../pha-contract-validation';
import {compareDescendingStrikes} from '../pha-option-strategy';
import {recognizeOptionOrders} from './pha-order-recognition';
import {optionContributionKey} from './pha-order-identity';

// THE CHANGE SET (CR-20260930-001 M2). Pure TypeScript, no Angular: the sheet as displayed and the
// session's BEFORE snapshot in, the CHANGES and their ORDER BLOCKS out (system description Â§6.2, Â§6.3),
// plus the ToS order text per block (Â§6.4) and the per-row marks the tables paint (yellow split
// cells, lock icons). Every rule that can be checked with numbers lives here so it runs standalone.
//
// Vocabulary (Â§9): a CHANGE is one difference between before and after on one instrument of one
// section; an ORDER BLOCK is the unit copied to ToS â€” one per stock change, option changes of a
// section packed together, at most 4 legs per ToS order and 4 orders (main + 3 linked) per copy.

/** One held position of the combo at import, as the Shell snapshot carries it (camelCase on the wire). */
export interface PhaSnapshotItem {
    tradingInstrumentCode: string;
    kind: number;         // 0 = Asset, 1 = Option
    netPosition: number;  // signed: long > 0, short < 0
    avgPx: number;
    groupBucketId: string | null;
    groupName: string | null;
}

/** The kind values of a Collars instrument (CollarsInstrumentKind). */
export const PHA_KIND_ASSET = 0;
export const PHA_KIND_OPTION = 1;

/**
 * One ToS order carries at most four legs. A BLOCK IS ONE ToS ORDER (owner 2026-09-30, vrequest: the
 * block is the minimum executable thing; copy is per block). Strategy orders are independent;
 * leftover legs are recognized before generic four-leg packing. Bulk export never links orders
 * merely because their underlying matches. Frozen legacy order text remains unchanged.
 */
export const PHA_LEGS_PER_ORDER = 4;
export const PHA_LEGS_PER_BLOCK = PHA_LEGS_PER_ORDER;

/**
 * A change as the builder sees it before it is packed: the trade that closes the gap on one sheet row
 * (or on several rows of the same contract â€” two Premium rows on one contract are one trade).
 */
export interface PhaChange {
    /** Explicit strategy memberships; independent legacy options have none. */
    strategyIds?: string[];
    strategyName?: string;
    allocationOffset?: number;
    rowKey: string;
    section: AnalysisSection;
    tradingInstrumentCode: string;
    kind: number;
    /** 1 = Buy, -1 = Sell: the direction of the TRADE (not of the resulting position). */
    side: 1 | -1;
    /** Always positive. */
    qty: number;
    strike: number | null;
    optionType: string;
    /** yyyy-MM-dd, empty for a stock. */
    expiration: string;
    /** The underlying ticker (AAPL, SPX): the ToS line's symbol. */
    underlying: string;
    /** What the row reads before and after, for the Collars pending view ("100" â†’ "221"). */
    beforeText: string;
    afterText: string;
    /** The row's current price (the option's mid or the stock's last), for the ToS limit; null = unpriced. */
    price: number | null;
    /** The signed positions the change bridges (for the export window's Before | After columns). */
    beforeQty: number;
    afterQty: number;
    /** The sheet rows this change came from (several when two rows sit on one contract). */
    rowIds: number[];
}

/** A block of changes as the panel submits it, plus what the panel needs to show it. */
export interface PhaBlock {
    blockId: string;
    section: AnalysisSection;
    sequence: number;
    changes: PhaChange[];
    /** The frozen state from the session for a locked block; Pending for a freshly built one. */
    status: AnalysisBlockStatus;
    orderText: string;
    exportedAtUtc: string;
    /** The underlying the block trades (a stock: its ticker; options: the contracts' underlying). */
    underlying: string;
    /** Options: the block's place in its underlying's chain â€” 0 = "Main Order", n = "Linked #n". Stocks: 0. */
    chainIndex: number;
    /** Pending orders are independent; frozen legacy trigger text remains untouched. */
    independent?: boolean;
    orderName?: string;
}

/** What the tables paint on one row (yellow split cells, the lock, the block it belongs to). */
export interface PhaRowMark {
    rowId: number;
    /** The row differs from the before state (it produces a change). */
    changed: boolean;
    /** The row's block is Exported / Overdue: no edits until revoked or acquired. */
    locked: boolean;
    /** Overdue: the red earmark. */
    overdue: boolean;
    status: AnalysisBlockStatus | null;
    blockId: string | null;
    /** The BEFORE value of each changed cell, as text; absent when the cell is unchanged. */
    before: {shares?: string; contracts?: string; strike?: string; expiration?: string};
    /** The trade the row produces, as the trader reads it ("SELL 3"). */
    tradeText: string;
}

/** The counts the header and the section badges show (Â§6.8): two different debts. */
export interface PhaPendingCounts {
    /** Blocks designed but not copied yet ("did I copy it?"). */
    notExported: number;
    /** Blocks copied but not acquired yet ("did I get filled and record it?"). */
    notAcquired: number;
    /** Of the not-acquired ones, those past the deadline. */
    overdue: number;
}

export interface PhaChangeSet {
    blocks: PhaBlock[];
    marks: {[rowId: number]: PhaRowMark};
    counts: {
        total: PhaPendingCounts;
        Underlyings: PhaPendingCounts;
        Premium: PhaPendingCounts;
        Protection: PhaPendingCounts;
    };
}

/** Mints and remembers ids: a change keeps its id across re-submits (the Shell keys locked blocks on it). */
export class PhaIdRegistry {
    private readonly _changeIds: {[rowKey: string]: string} = {};
    private readonly _blockIds: {[blockKey: string]: string} = {};

    changeIdFor(rowKey: string): string {
        if (!this._changeIds[rowKey]) {
            this._changeIds[rowKey] = newGuid();
        }
        return this._changeIds[rowKey];
    }

    /** A pending block keeps its id while its content (section + change ids) is the same. */
    blockIdFor(section: AnalysisSection, changeIds: string[]): string {
        const key = section + ':' + changeIds.slice().sort().join(',');
        if (!this._blockIds[key]) {
            this._blockIds[key] = newGuid();
        }
        return this._blockIds[key];
    }

    /**
     * Seed from a restored session so the ids the Shell holds are the ids the panel uses. An id that
     * belongs to a DEAD block (Acquired / Removed / Orphaned â€” the Shell keeps it as the record) is
     * BURNED: a later change on the same row gets a fresh id, never the record's. Live blocks are
     * seeded last so theirs win.
     */
    seed(blocks: AnalysisBlockDto[]): void {
        const all = blocks || [];
        const isLive = (b: AnalysisBlockDto) => b.status === 'Pending' || b.status === 'Exported' || b.status === 'Overdue';
        all.filter(b => !isLive(b)).forEach(b => {
            const ids: string[] = [];
            (b.changes || []).forEach(c => {
                ids.push(c.changeId);
                if (this._changeIds[c.rowKey] === c.changeId) {
                    delete this._changeIds[c.rowKey];
                }
            });
            const key = b.section + ':' + ids.slice().sort().join(',');
            if (this._blockIds[key] === b.blockId) {
                delete this._blockIds[key];
            }
        });
        all.filter(isLive).forEach(b => {
            const ids: string[] = [];
            (b.changes || []).forEach(c => {
                this._changeIds[c.rowKey] = c.changeId;
                ids.push(c.changeId);
            });
            this._blockIds[b.section + ':' + ids.slice().sort().join(',')] = b.blockId;
        });
    }
}

/** RFC 4122 v4, from the browser's crypto when present (a test runner has none: Math.random then). */
export function newGuid(): string {
    const c: any = typeof crypto !== 'undefined' ? crypto : null;
    if (c && typeof c.randomUUID === 'function') {
        return c.randomUUID();
    }
    const bytes: number[] = [];
    if (c && typeof c.getRandomValues === 'function') {
        const arr = new Uint8Array(16);
        c.getRandomValues(arr);
        for (let i = 0; i < 16; i++) {
            bytes.push(arr[i]);
        }
    } else {
        for (let i = 0; i < 16; i++) {
            bytes.push(Math.floor(Math.random() * 256));
        }
    }
    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;
    const hex = bytes.map(b => (b < 16 ? '0' : '') + b.toString(16));
    return hex.slice(0, 4).join('') + '-' + hex.slice(4, 6).join('') + '-' + hex.slice(6, 8).join('') + '-'
        + hex.slice(8, 10).join('') + '-' + hex.slice(10, 16).join('');
}

/** The sheet row's key: section + instrument â€” "AAPL shares 100 â†’ 221" is the same change however often it is re-displayed. */
export function rowKeyOf(section: AnalysisSection, tradingInstrumentCode: string): string {
    return section + '|' + tradingInstrumentCode;
}

/** Allocations belong to a single source contribution, even after quantity splitting. */
function contributionKey(key: string): string { return key.split('|allocation:')[0]; }

const SECTION_ORDER: AnalysisSection[] = ['Underlyings', 'Premium', 'Protection'];

/** The section a snapshot item belongs to, by its Collars bucket NAME (owner ruling: by name only). */
export function sectionOfSnapshotItem(item: PhaSnapshotItem): AnalysisSection | null {
    if (item.kind === PHA_KIND_ASSET) {
        return 'Underlyings';
    }
    const name = (item.groupName || '').toLowerCase();
    return name === 'underlyings' ? 'Underlyings' : name === 'premium' ? 'Premium' : (name === 'protection' ? 'Protection' : null);
}

/**
 * Build the change set: diff the sheet against the before snapshot, pack the changes into blocks,
 * carry the session's LOCKED blocks through unchanged, mark every row.
 *
 * @param sheet         the sheet as displayed
 * @param snapshot      the session's before snapshot (empty for a fresh start)
 * @param sessionBlocks the session's blocks as last read (locked ones are kept verbatim; the rest is rebuilt)
 * @param ids           the id registry (stable change ids)
 */
export function buildChangeSet(sheet: PhaSheet, snapshot: PhaSnapshotItem[], sessionBlocks: AnalysisBlockDto[],
                               ids: PhaIdRegistry): PhaChangeSet {
    // The before state = the snapshot at import PLUS every change already ACQUIRED in this session:
    // an acquired fill is in the book, so its row reads unchanged until the trader edits it again
    // (else the same order would be offered for copy a second time).
    const acquired = (sessionBlocks || []).filter(b => b.status === 'Acquired');
    const before = indexSnapshot(snapshot || [], acquired);
    const locked = (sessionBlocks || []).filter(b => b.status === 'Exported' || b.status === 'Overdue');
    const lockedRowKeys: {[rowKey: string]: AnalysisBlockDto} = {};
    locked.forEach(b => (b.changes || []).forEach(c => lockedRowKeys[c.rowKey] = b));
    const lockedQuantity: {[key: string]: number} = {};
    locked.forEach(b => (b.changes || []).forEach(c => {
        const key = contributionKey(c.rowKey);
        lockedQuantity[key] = (lockedQuantity[key] || 0) + c.side * c.qty;
    }));

    // 1. Every candidate change, from the rows (rows on one contract merged only within their strategy contribution), keyed by rowKey.
    const candidates = collectChanges(sheet, before, snapshot || [], acquired);
    const safeTwoForOneReduction = (row: PhaPremiumRow) => sheet.twoForOneReduction(row)
        && candidates.filter(c => sheet.strategyRows('premium', row.id).some(r => c.rowIds.indexOf(r.id) >= 0))
            .every(c => c.beforeQty * c.afterQty >= 0 && Math.abs(c.afterQty) <= Math.abs(c.beforeQty));
    const optionRows = [...sheet.premiumRows, ...sheet.protectionRows];
    const incomplete: {[id: string]: true} = {};
    optionRows.forEach(r => {
        const invalidTwoForOne = r.strategyType === 'Two-for-One' && (!('ticker' in r)
            || !!sheet.twoForOneProblem(r as PhaPremiumRow) && !safeTwoForOneReduction(r as PhaPremiumRow));
        if (r.strategyId && !r.crossedOut && (!r.expiration || r.strike === null || !r.optionTicker || invalidTwoForOne)) {
            incomplete[r.strategyFamilyId || r.strategyId] = true;
        }
    });
    const blockedGroups = optionRows.filter(r => incomplete[r.strategyFamilyId || r.strategyId]).map(r => r.strategyId);
    candidates.forEach(c => {
        c.strategyIds = optionRows.filter(r => c.rowIds.indexOf(r.id) >= 0 && !!r.strategyId)
            .map(r => r.strategyId).filter((id, i, all) => all.indexOf(id) === i);
        c.strategyName = (optionRows.find(r => c.rowIds.indexOf(r.id) >= 0 && r.strategyType) || {}).strategyType;
    });

    // 2. Rows: which candidate they feed, and the before values of their cells.
    const marks: {[rowId: number]: PhaRowMark} = {};
    const rowToKey: {[rowId: number]: string} = {};
    candidates.forEach(c => c.rowIds.forEach(id => rowToKey[id] = c.rowKey));

    // 3. Pending blocks: the candidates NOT covered by a locked block, packed per section.
    const blocks: PhaBlock[] = [];
    let sequence = 0;
    locked.forEach(b => {
        const lockedChanges = (b.changes || []).map(c => lockedChangeOf(c, candidates)).sort(compareDescendingStrikes);
        blocks.push({
            blockId: b.blockId,
            section: b.section,
            sequence: 0,
            status: b.status,
            independent: (b.orderText || '').indexOf(' TRG BY') < 0,
            orderText: b.orderText || '',
            exportedAtUtc: b.exportedAtUtc || '',
            underlying: '',
            chainIndex: 0,
            orderName: lockedChanges.length && lockedChanges.every(c => c.strategyName === 'Two-for-One') ? 'Two-for-One' : undefined,
            changes: lockedChanges
        });
    });
    const duplicateDesign = sheet.contractValidationEnabled && duplicateContracts(sheet).length > 0;
    SECTION_ORDER.forEach(section => {
        const free = candidates.filter(c => !duplicateDesign && c.section === section && !lockedRowKeys[c.rowKey]
            && !sheet.protectionRows.some(r => c.rowIds.indexOf(r.id) >= 0 && r.strategyType === 'Two-for-One')
            && !sheet.premiumRows.some(r => c.rowIds.indexOf(r.id) >= 0 && !r.crossedOut && !!sheet.twoForOneProblem(r) && !safeTwoForOneReduction(r))
            && !lockedRowKeys[rowKeyOf(c.section, c.tradingInstrumentCode)]
            && !(c.strategyIds || []).some(id => blockedGroups.indexOf(id) >= 0))
            .map(c => {
                const delta = c.side * c.qty - (lockedQuantity[c.rowKey] || 0);
                const beforeQty = c.beforeQty + (lockedQuantity[c.rowKey] || 0);
                return {...c, side: (delta > 0 ? 1 : -1) as 1 | -1, qty: Math.abs(delta),
                    beforeQty, beforeText: lockedQuantity[c.rowKey] ? signedText(beforeQty) : c.beforeText, allocationOffset: Math.abs(lockedQuantity[c.rowKey] || 0)};
            }).filter(c => c.qty > 0);
        packSection(section, free, ids).forEach(b => blocks.push(b));
    });
    // Sequence: the section order, then ALPHABETICAL by underlying name inside the section, copied or
    // not (owner 2026-09-30: a copied block never jumps). Within each underlying,
    // highest strikes come first; expiration only breaks equal-strike ties.
    const highestLeg = (b: PhaBlock): PhaChange => b.changes.slice().sort(compareDescendingStrikes)[0];
    SECTION_ORDER.forEach(section => {
        const inSection = blocks.filter(b => b.section === section).sort((a, b) => {
            const first = highestLeg(a), second = highestLeg(b);
            return (first ? first.underlying : '').localeCompare(second ? second.underlying : '')
                || compareDescendingStrikes(first || {strike: null}, second || {strike: null})
                || (first ? first.expiration : '').localeCompare(second ? second.expiration : '');
        });
        // The chain: an underlying's option blocks in this order â€” the first is the main order.
        const seen: {[underlying: string]: number} = {};
        inSection.forEach(b => {
            b.sequence = ++sequence;
            b.underlying = b.changes.length ? b.changes[0].underlying : '';
            const isOption = b.changes.length > 0 && b.changes[0].kind === PHA_KIND_OPTION;
            b.chainIndex = isOption ? (seen[b.underlying] = (seen[b.underlying] === undefined ? 0 : seen[b.underlying] + 1)) : 0;
        });
    });
    blocks.sort((a, b) => a.sequence - b.sequence);

    // 4. Marks, from the blocks (a row in no block is unchanged).
    const blockOfKey: {[rowKey: string]: PhaBlock} = {};
    blocks.forEach(b => b.changes.forEach(c => {
        const key = contributionKey(c.rowKey);
        const old = blockOfKey[key];
        if (!old || b.status === 'Overdue' || (b.status === 'Exported' && old.status !== 'Overdue')) {
            blockOfKey[key] = b;
        }
    }));
    const markRow = (rowId: number, beforeValues: PhaRowMark['before']) => {
        const key = rowToKey[rowId];
        const candidate = key ? candidates.find(c => c.rowKey === key) : null;
        const block = key ? (blockOfKey[key] || (candidate && blockOfKey[rowKeyOf(candidate.section, candidate.tradingInstrumentCode)])) : null;
        const change = candidate;
        const isLocked = !!block && (block.status === 'Exported' || block.status === 'Overdue');
        marks[rowId] = {
            rowId,
            changed: !!change,
            locked: isLocked,
            overdue: !!block && block.status === 'Overdue',
            status: block ? block.status : null,
            blockId: block ? block.blockId : null,
            before: change ? (change.kind === PHA_KIND_OPTION ? {contracts: signedText(change.beforeQty)} : beforeValues) : {},
            tradeText: change ? (change.side > 0 ? 'BUY ' : 'SELL ') + change.qty : ''
        };
    };
    sheet.rows.forEach(r => {
        if (sheet.isExpired(r)) { return; }
        const item = r.assetType === 'Call' ? before.bySectionCode[rowKeyOf('Underlyings', r.ticker)] : before.byCode[r.ticker];
        const beforeShares = item ? item.netPosition : 0;
        markRow(r.id, beforeShares !== afterShares(r) ? {shares: String(beforeShares)} : {});
    });
    sheet.premiumRows.filter(p => !sheet.isExpired(p)).forEach(p => markRow(p.id, optionBefore(p.optionTicker, p.origin, before, 'Premium')));
    sheet.protectionRows.filter(p => !sheet.isExpired(p)).forEach(p => markRow(p.id, optionBefore(p.optionTicker, p.origin, before, 'Protection')));

    // 5. Counts, from the block statuses only (nothing stores a count â€” Â§5).
    const counts = {
        total: countBlocks(blocks),
        Underlyings: countBlocks(blocks.filter(b => b.section === 'Underlyings')),
        Premium: countBlocks(blocks.filter(b => b.section === 'Premium')),
        Protection: countBlocks(blocks.filter(b => b.section === 'Protection'))
    };

    return {blocks, marks, counts};
}

/** The wire shape of the pending + locked blocks, for ReplaceAnalysisChanges. */
export function toBlockInputs(blocks: PhaBlock[], ids: PhaIdRegistry): AnalysisBlockInput[] {
    return blocks
        .filter(b => b.status === 'Pending' || b.status === 'Exported' || b.status === 'Overdue')
        .map(b => ({
            blockId: b.blockId,
            section: b.section,
            sequence: b.sequence,
            changes: b.changes.map(c => toChangeInput(c, ids))
        }));
}

function toChangeInput(c: PhaChange, ids: PhaIdRegistry): AnalysisChangeInput {
    return {
        changeId: ids.changeIdFor(c.rowKey),
        rowKey: c.rowKey,
        section: c.section,
        tradingInstrumentCode: c.tradingInstrumentCode,
        kind: c.kind,
        side: c.side,
        qty: c.qty,
        strike: c.strike,
        optionType: c.optionType || '',
        expiration: c.expiration || '',
        beforeText: c.beforeText,
        afterText: c.afterText
    };
}

// ---- the diff -----------------------------------------------------------------------------------

interface SnapshotIndex {
    /** Stocks by ticker (kind 0). */
    byCode: {[code: string]: PhaSnapshotItem};
    /** Options by section + option ticker. */
    bySectionCode: {[key: string]: PhaSnapshotItem};
}

/** The same book baseline used by the order diff, including fills acquired in this session. */
export function optionHoldingsOf(items: PhaSnapshotItem[], blocks: AnalysisBlockDto[]): PhaOptionHoldings {
    const index = indexSnapshot(items || [], (blocks || []).filter(b => b.status === 'Acquired'));
    const holdings: PhaOptionHoldings = {};
    Object.keys(index.bySectionCode).forEach(key => holdings[key] = index.bySectionCode[key].netPosition);
    return holdings;
}

/** Book ownership per strategy contribution; aggregate contract holdings cannot identify shared strategy owners. */
export function optionContributionsOf(sheet: PhaSheet, items: PhaSnapshotItem[], blocks: AnalysisBlockDto[]): PhaOptionHoldings {
    const snapshot = items || [], acquired = (blocks || []).filter(b => b.status === 'Acquired');
    const contributions: PhaOptionHoldings = {};
    collectChanges(sheet, indexSnapshot(snapshot, acquired), snapshot, acquired, contributions);
    return contributions;
}

function indexSnapshot(items: PhaSnapshotItem[], acquired: AnalysisBlockDto[]): SnapshotIndex {
    const idx: SnapshotIndex = {byCode: {}, bySectionCode: {}};
    items.forEach(i => {
        if (i.kind === PHA_KIND_ASSET) {
            const prev = idx.byCode[i.tradingInstrumentCode];
            idx.byCode[i.tradingInstrumentCode] = prev ? {...prev, netPosition: prev.netPosition + i.netPosition} : {...i};
        } else {
            const section = sectionOfSnapshotItem(i);
            if (!section) {
                return; // an option in no Premium/Protection bucket is not part of the analysis (item 10)
            }
            const key = rowKeyOf(section, i.tradingInstrumentCode);
            const prev = idx.bySectionCode[key];
            idx.bySectionCode[key] = prev ? {...prev, netPosition: prev.netPosition + i.netPosition} : {...i};
        }
    });
    // The acquired fills: signed (buy +, sell -) onto the position they landed on.
    (acquired || []).forEach(b => (b.changes || []).forEach(c => {
        const signed = (c.side >= 0 ? 1 : -1) * c.qty;
        if (c.kind === PHA_KIND_ASSET) {
            const prev = idx.byCode[c.tradingInstrumentCode];
            idx.byCode[c.tradingInstrumentCode] = prev ? {...prev, netPosition: prev.netPosition + signed}
                : {tradingInstrumentCode: c.tradingInstrumentCode, kind: PHA_KIND_ASSET, netPosition: signed, avgPx: c.fillPx || 0, groupBucketId: c.groupBucketId, groupName: 'Underlyings'};
        } else {
            const key = rowKeyOf(c.section, c.tradingInstrumentCode);
            const prev = idx.bySectionCode[key];
            idx.bySectionCode[key] = prev ? {...prev, netPosition: prev.netPosition + signed}
                : {tradingInstrumentCode: c.tradingInstrumentCode, kind: PHA_KIND_OPTION, netPosition: signed, avgPx: c.fillPx || 0, groupBucketId: c.groupBucketId, groupName: c.section};
        }
    }));
    return idx;
}

function afterShares(r: PhaUnderlyingRow): number {
    return r.crossedOut ? 0 : Math.max(0, Math.floor(r.shares || 0));
}

/** A row's signed after position: SELL = short (negative). Zero when crossed out or without a contract. */
function afterContracts(side: 'Buy' | 'Sell', contracts: number, crossedOut: boolean, optionTicker: string | null): number {
    if (crossedOut || !optionTicker) {
        return 0;
    }
    const n = Math.max(0, Math.floor(contracts || 0));
    return side === 'Sell' ? -n : n;
}

function collectChanges(sheet: PhaSheet, before: SnapshotIndex, snapshot: PhaSnapshotItem[], acquired: AnalysisBlockDto[], contributions?: PhaOptionHoldings): PhaChange[] {
    const out: PhaChange[] = [];

    // Underlyings: one instrument per row (the sheet keeps one row per ticker).
    sheet.rows.forEach(r => {
        if (sheet.isExpired(r)) { return; }
        const item = r.assetType === 'Call' ? before.bySectionCode[rowKeyOf('Underlyings', r.ticker)] : before.byCode[r.ticker];
        const beforeQty = item ? item.netPosition : 0;
        const afterQty = afterShares(r);
        const delta = afterQty - beforeQty;
        if (delta === 0) {
            return;
        }
        out.push({
            rowKey: r.assetType === 'Call' ? optionContributionKey('Underlyings', r.ticker) : rowKeyOf('Underlyings', r.ticker),
            section: 'Underlyings',
            tradingInstrumentCode: r.ticker,
            kind: r.assetType === 'Call' ? PHA_KIND_OPTION : PHA_KIND_ASSET,
            side: delta > 0 ? 1 : -1,
            qty: Math.abs(delta),
            strike: r.assetType === 'Call' ? r.callStrike : null,
            optionType: r.assetType === 'Call' ? 'Call' : '',
            expiration: r.callExpirationDate || '',
            underlying: r.callUnderlying || r.ticker,
            beforeText: String(beforeQty),
            afterText: String(afterQty),
            price: r.price,
            beforeQty,
            afterQty,
            rowIds: [r.id]
        });
    });

    // Preserve strategy contributions before netting contracts; loose rows remain one contract pool.
    const rows = [...sheet.premiumRows.map(row => ({row, section: 'Premium' as AnalysisSection})),
        ...sheet.protectionRows.map(row => ({row, section: 'Protection' as AnalysisSection}))];
    const slots: {[key: string]: {qty: number, rowIds: number[], row: PhaPremiumRow | PhaProtectionRow,
        section: AnalysisSection, contractKey: string, held: number}} = {};
    rows.forEach(({row, section}) => {
        if (!row.optionTicker || sheet.isExpired(row)) { return; }
        const contractKey = rowKeyOf(section, row.optionTicker);
        const key = optionContributionKey(section, row.optionTicker, row.strategyId);
        const slot = slots[key] || (slots[key] = {qty: 0, rowIds: [], row, section, contractKey, held: 0});
        const quantity = 'contractsSelling' in row ? row.contractsSelling : row.contracts;
        slot.qty += afterContracts(row.side, quantity, row.crossedOut, row.optionTicker);
        slot.rowIds.push(row.id);
        if (row.origin === 'held') {
            slot.held += (row.side === 'Sell' ? -1 : 1) * ('heldContracts' in row ? row.heldContracts : quantity);
        }
    });
    const baseline: {[key: string]: number} = {};
    const acquiredByContract: {[key: string]: number} = {};
    acquired.forEach(b => (b.changes || []).filter(c => c.kind === PHA_KIND_OPTION).forEach(c => {
        const key = contributionKey(c.rowKey);
        baseline[key] = (baseline[key] || 0) + c.side * c.qty;
        const contractKey = rowKeyOf(c.section, c.tradingInstrumentCode);
        acquiredByContract[contractKey] = (acquiredByContract[contractKey] || 0) + c.side * c.qty;
    }));
    const initial = indexSnapshot(snapshot, []);
    Object.keys(initial.bySectionCode).forEach(contractKey => {
        const keys = Object.keys(slots).filter(key => slots[key].contractKey === contractKey);
        if (!keys.length) { return; }
        let remaining = initial.bySectionCode[contractKey].netPosition;
        const sign = remaining < 0 ? -1 : 1;
        const owners = keys.filter(key => slots[key].held * sign > 0);
        owners.forEach(key => {
            const desired = Math.max(0, (slots[key].held - (baseline[key] || 0)) * sign);
            const quantity = Math.min(Math.abs(remaining), desired) * sign;
            baseline[key] = (baseline[key] || 0) + quantity;
            remaining -= quantity;
        });
        const owner = owners[0] || keys.find(key => !slots[key].row.strategyId) || keys[0];
        baseline[owner] = (baseline[owner] || 0) + remaining;
    });
    // Legacy acquisitions were contract-netted. Attribute their holdings to existing held rows;
    // new contribution-aware acquisitions already identify the exact strategy.
    Object.keys(acquiredByContract).forEach(contractKey => {
        const keys = Object.keys(slots).filter(key => slots[key].contractKey === contractKey);
        if (baseline[contractKey] && keys.length) {
            const owner = keys.find(key => slots[key].held !== 0) || keys[0];
            baseline[owner] = (baseline[owner] || 0) + baseline[contractKey];
            delete baseline[contractKey];
        }
    });
    Object.keys(slots).forEach(key => {
        const slot = slots[key], row = slot.row;
        const beforeQty = baseline[key] || 0;
        if (contributions) { contributions[key] = beforeQty; }
        const delta = slot.qty - beforeQty;
        if (!delta) { return; }
        const ticker = parseTicker(row.optionTicker);
        out.push({
            rowKey: key, section: slot.section, tradingInstrumentCode: row.optionTicker,
            kind: PHA_KIND_OPTION, side: delta > 0 ? 1 : -1, qty: Math.abs(delta),
            strike: row.strike, optionType: row.optionType,
            expiration: row.expiration ? row.expiration.date : ticker.expiration,
            underlying: ticker.underlying, beforeText: signedText(beforeQty), afterText: signedText(slot.qty),
            price: row.price, beforeQty, afterQty: slot.qty, rowIds: slot.rowIds
        });
    });

    return out;
}

/** "+3" / "-3" / "0": a signed position as the trader reads it. */
export function signedText(qty: number): string {
    return qty > 0 ? '+' + qty : String(qty);
}

/**
 * The before values of an option row's cells: a HELD row that changed shows its held contracts on the
 * left of the split cell. A row's strike/expiration are picked from the chain and cannot differ from
 * the held contract while the row keeps its ticker (a re-pick makes it another contract: the held one
 * goes to 0, the new one from 0), so only the contracts cell splits.
 */
function optionBefore(optionTicker: string | null, origin: string, before: SnapshotIndex, section: AnalysisSection): PhaRowMark['before'] {
    if (!optionTicker) {
        return {};
    }
    const item = before.bySectionCode[rowKeyOf(section, optionTicker)];
    return item ? {contracts: signedText(item.netPosition)} : {contracts: '0'};
}

/** The option ticker parts the builder needs; tolerant of the platform format "<root> <underlying> <type> <strike> <yyyy-MM-dd>". */
function parseTicker(optionTicker: string): {underlying: string, expiration: string} {
    const parts = (optionTicker || '').split(' ');
    return {underlying: parts.length > 1 ? parts[1] : (parts[0] || ''), expiration: parts.length > 4 ? parts[4] : ''};
}

/** A locked block's change, re-hydrated from the wire (its rows may still be on the sheet for the display bits). */
function lockedChangeOf(c: AnalysisBlockDto['changes'][0], candidates: PhaChange[]): PhaChange {
    const live = candidates.find(x => x.rowKey === contributionKey(c.rowKey))
        || candidates.find(x => x.section === c.section && x.tradingInstrumentCode === c.tradingInstrumentCode);
    const ticker = c.kind === PHA_KIND_OPTION ? parseTicker(c.tradingInstrumentCode) : {underlying: c.tradingInstrumentCode, expiration: ''};
    return {
        strategyName: live ? live.strategyName : undefined,
        strategyIds: live ? live.strategyIds : undefined,
        rowKey: c.rowKey,
        section: c.section,
        tradingInstrumentCode: c.tradingInstrumentCode,
        kind: c.kind,
        side: c.side >= 0 ? 1 : -1,
        qty: c.qty,
        strike: c.strike,
        optionType: c.optionType || '',
        expiration: c.expiration || ticker.expiration,
        underlying: ticker.underlying,
        beforeText: c.beforeText,
        afterText: c.afterText,
        price: live ? live.price : null,
        beforeQty: live ? live.beforeQty : parseInt(c.beforeText, 10) || 0,
        afterQty: live ? live.afterQty : parseInt(c.afterText, 10) || 0,
        rowIds: live ? live.rowIds : []
    };
}

// ---- packing (Â§6.3) -----------------------------------------------------------------------------

/**
 * Stocks: one block each, never shared. Options: PER UNDERLYING (one underlying per ToS order), the
 * existing strategies get one order each. Recognize leftover condors, butterflies and verticals,
 * then pack the remaining legs into independent combos of at most four legs (owner 2026-10-07).
 */
function packSection(section: AnalysisSection, changes: PhaChange[], ids: PhaIdRegistry): PhaBlock[] {
    const out: PhaBlock[] = [];
    const stocks = changes.filter(c => c.kind === PHA_KIND_ASSET).sort((a, b) => a.tradingInstrumentCode.localeCompare(b.tradingInstrumentCode));
    stocks.forEach(c => out.push(pendingBlock(section, [c], ids)));
    const options = changes.filter(c => c.kind === PHA_KIND_OPTION).sort(byUnderlyingExpirationStrike);
    const byUnderlying: {[u: string]: PhaChange[]} = {};
    const order: string[] = [];
    options.forEach(c => {
        if (!byUnderlying[c.underlying]) {
            byUnderlying[c.underlying] = [];
            order.push(c.underlying);
        }
        byUnderlying[c.underlying].push(c);
    });
    order.forEach(u => {
        const legs = byUnderlying[u];
        const strategies = new Map<string, PhaChange[]>();
        const loose: PhaChange[] = [];
        legs.forEach(leg => {
            const strategyId = (leg.strategyIds || [])[0];
            if (!strategyId) { loose.push(leg); return; }
            if (!strategies.has(strategyId)) { strategies.set(strategyId, []); }
            strategies.get(strategyId).push(leg);
        });
        strategies.forEach(unit => {
            // Contract netting is local to this strategy, never across strategy boundaries.
            if (unit.length <= PHA_LEGS_PER_BLOCK) {
                const block = pendingBlock(section, unit, ids);
                block.orderName = unit[0].strategyName || (unit.length === 3 ? 'Butterfly' : unit.length === 2 ? 'Vertical' : 'Strategy');
                out.push(block);
            }
        });
        recognizeOptionOrders(loose).forEach(order => {
            const block = pendingBlock(section, order.changes, ids);
            block.orderName = order.name;
            out.push(block);
        });
    });
    return out;
}

function pendingBlock(section: AnalysisSection, changes: PhaChange[], ids: PhaIdRegistry): PhaBlock {
    return {
        blockId: ids.blockIdFor(section, changes.map(c => ids.changeIdFor(c.rowKey))),
        section,
        sequence: 0,
        status: 'Pending',
        independent: true,
        orderText: '',
        exportedAtUtc: '',
        underlying: changes.length ? changes[0].underlying : '',
        chainIndex: 0,
        changes: changes.slice().sort(compareDescendingStrikes)
    };
}

/** Strategy/combo label for independent orders; legacy linked labels remain available. */
export function chainName(block: PhaBlock): string {
    if (!block.changes.length || block.changes[0].kind !== PHA_KIND_OPTION) {
        return '';
    }
    return block.orderName || (block.independent ? 'Combo' : block.chainIndex === 0 ? 'Main Order' : 'Linked #' + block.chainIndex);
}

function byUnderlyingExpirationStrike(a: PhaChange, b: PhaChange): number {
    return a.underlying.localeCompare(b.underlying)
        || compareDescendingStrikes(a, b)
        || a.expiration.localeCompare(b.expiration)
        || a.optionType.localeCompare(b.optionType);
}

function countBlocks(blocks: PhaBlock[]): PhaPendingCounts {
    return {
        notExported: blocks.filter(b => b.status === 'Pending' && b.changes.length > 0).length,
        notAcquired: blocks.filter(b => b.status === 'Exported' || b.status === 'Overdue').length,
        overdue: blocks.filter(b => b.status === 'Overdue').length
    };
}

// ---- the ToS order text (Â§6.4) ------------------------------------------------------------------
//
// OPTIONS: the platform's ToS Desktop custom-order grammar, verbatim from the APG/Collars copy path
// (CopyOrdersToService.makeEquityTosDesktopOrderLine): one line per block (= one ToS order of up to
// 4 legs). Copied as a CHAIN (an underlying's main + linked blocks in one write) every linked line is
// suffixed " TRG BY"; copied ALONE a linked block is a plain line â€” there is nothing to trigger it
// (owner 2026-09-30). Quantities are SIGNED per leg (buy +, sell -), the order quantity is 1, the
// limit is left blank for the trader to type in ToS (the Collars copy path passes none either).
//
// STOCKS: no line exists in the platform yet. ASSUMED [my call] until the owner brings a real one:
//     BUY +100 AAPL @231.50 LMT   /   SELL -100 AAPL @231.50 LMT
// (owner 2026-09-30: build on the assumption, correct at smoke).

/**
 * The order text of one block (one line); empty when a leg cannot be written (the caller refuses the
 * copy). `triggered` = the block is a linked order copied WITH its main: the line carries " TRG BY".
 */
export function composeOrderText(block: PhaBlock, triggered = false): string {
    if (!block || !block.changes.length) {
        return '';
    }
    if (block.changes[0].kind === PHA_KIND_ASSET) {
        return stockOrderLine(block.changes[0]);
    }
    const line = optionOrderLine(block.changes);
    return line ? (triggered ? line + ' TRG BY' : line) : '';
}

/**
 * Bulk copy: new orders remain independent. Explicit legacy callers may still request linked lines.
 * Returns the text per block (what the Shell freezes) and the joined clipboard text; empty text
 * when any block cannot be written.
 */
export function composeChainText(blocks: PhaBlock[]): {perBlock: {blockId: string, orderText: string}[], text: string} {
    const sorted = blocks.slice().sort((a, b) => a.chainIndex - b.chainIndex);
    const perBlock = sorted.map((b, i) => ({blockId: b.blockId, orderText: composeOrderText(b, !b.independent && i > 0)}));
    const text = perBlock.some(p => !p.orderText) ? '' : perBlock.map(p => p.orderText).join('\n');
    return {perBlock, text};
}

export function stockOrderLine(c: PhaChange): string {
    const side = c.side > 0 ? 'BUY' : 'SELL';
    const qty = (c.side > 0 ? '+' : '-') + c.qty;
    const px = c.price !== null && isFinite(c.price) && c.price > 0 ? '@' + money(c.price) : '@';
    return `${side} ${qty} ${c.tradingInstrumentCode} ${px} LMT`;
}

export function optionOrderLine(legs: PhaChange[]): string {
    if (!legs.length || legs.length > PHA_LEGS_PER_ORDER || legs.some(l => l.kind !== PHA_KIND_OPTION || l.strike === null || !l.expiration || !l.optionType)) {
        return '';
    }
    const underlying = legs[0].underlying;
    if (legs.some(l => l.underlying !== underlying)) {
        return ''; // one underlying per ToS custom order; the packer keeps them apart by sorting, this is the guard
    }
    legs = legs.slice().sort(compareDescendingStrikes);
    const quantities = legs.map(l => (l.side > 0 ? '+' : '-') + l.qty).join('/');
    const dates = legs.map(l => tosDate(l.expiration)).join('/');
    const strikes = legs.map(l => String(l.strike)).join('/');
    const types = legs.map(l => l.optionType.toUpperCase()).join('/');
    // The limit is left for the trader to type, exactly as the Collars copy path does (it passes no limit).
    const lpx = '@';
    const weeklys = underlying === 'SPX' ? ' (Weeklys)' : '';
    return `BUY 1 ${quantities} CUSTOM ${underlying} 100${weeklys} ${dates} ${strikes} ${types} ${lpx} LMT GTC`;
}

/** yyyy-MM-dd â†’ "17 OCT 26" (the ToS date form the APG line uses). */
export function tosDate(iso: string): string {
    const d = DateTime.fromISO(iso);
    return d.isValid ? d.toFormat('dd MMM yy').toUpperCase() : iso;
}

function money(v: number): string {
    const sign = v < 0 ? '-' : '';
    const abs = Math.abs(v);
    const whole = Math.floor(abs);
    const cents = Math.round((abs - whole) * 100);
    const wholeText = whole.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return sign + wholeText + '.' + (cents < 10 ? '0' : '') + cents;
}

// The APG line divides every leg by the highest common factor and multiplies the order quantity
// instead (isPriceToOpen). Collars' path passes the raw quantities with an order quantity of 1 â€” this
// builder does the same: the fills come back per leg, one per change, so the quantities must read as
// the changes do.
